import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, Globe, Link2, ListFilter, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
    createRedirectGroup,
    updateRedirectGroup,
    addRedirectGroupItem,
    updateRedirectGroupItem,
    deleteRedirectGroupItem,
} from '@/services/api/redirect-groups';
import { getDomains } from '@/services/api/domains';
import UrlSelectorDialog from './UrlSelectorDialog';
import type {
    RedirectGroup,
    RedirectGroupCreate,
    RedirectGroupItemCreate,
    RedirectType,
    SyncMode,
    Period,
    ItemType,
    AllowedPath,
} from '@/types/redirect-group';

interface RedirectGroupFormProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    group?: RedirectGroup | null;
}

interface ItemDraft {
    id?: number; // existe se ja salvo no DB
    item_type: ItemType;
    domain: string;
    manual_url: string;
    allowed_paths: AllowedPath[];
    weight: number;
    is_active: boolean;
    _deleted?: boolean;
}

const emptyItem = (): ItemDraft => ({
    item_type: 'domain',
    domain: '',
    manual_url: '',
    allowed_paths: [],
    weight: 1,
    is_active: true,
});

export default function RedirectGroupForm({ open, onOpenChange, group }: RedirectGroupFormProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const isEdit = !!group;

    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [redirectType, setRedirectType] = useState<RedirectType>('smart');
    const [syncMode, setSyncMode] = useState<SyncMode>('rps');
    const [period, setPeriod] = useState<Period>('yesterday');
    const [minImpressions, setMinImpressions] = useState(0);
    const [minPageviews, setMinPageviews] = useState(0);
    const [defaultUrl, setDefaultUrl] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [items, setItems] = useState<ItemDraft[]>([]);

    // URL selector state
    const [urlSelectorOpen, setUrlSelectorOpen] = useState(false);
    const [urlSelectorItemIndex, setUrlSelectorItemIndex] = useState<number | null>(null);

    // Lista de dominios disponiveis (do ms-api-system)
    const { data: availableDomains = [] } = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
    });

    useEffect(() => {
        if (open && group) {
            setName(group.name || '');
            setSlug(group.slug || '');
            setRedirectType(group.redirect_type);
            setSyncMode((group.sync_mode as SyncMode) || 'rps');
            setPeriod(group.period);
            setMinImpressions(group.min_impressions || 0);
            setMinPageviews(group.min_pageviews || 0);
            setDefaultUrl(group.default_url || '');
            setIsActive(group.is_active);
            setItems(
                (group.items || []).map((it) => {
                    // Normaliza allowed_paths: aceita formato legacy (string[]) ou novo (AllowedPath[])
                    let allowed: AllowedPath[] = [];
                    if (Array.isArray(it.allowed_paths)) {
                        allowed = it.allowed_paths.map((ap: any) => {
                            if (typeof ap === 'string') {
                                return { path: ap, weight: null };
                            }
                            return {
                                path: ap.path || '',
                                weight: ap.weight ?? null,
                            };
                        }).filter((ap) => ap.path);
                    }
                    return {
                        id: it.id,
                        item_type: it.item_type,
                        domain: it.domain || '',
                        manual_url: it.manual_url || '',
                        allowed_paths: allowed,
                        weight: it.weight,
                        is_active: it.is_active,
                    };
                })
            );
        } else if (open && !group) {
            setName('');
            setSlug('');
            setRedirectType('smart');
            setSyncMode('rps');
            setPeriod('yesterday');
            setMinImpressions(0);
            setMinPageviews(0);
            setDefaultUrl('');
            setIsActive(true);
            setItems([emptyItem()]);
        }
    }, [open, group]);

    // Auto-gera slug a partir do nome (apenas no create)
    useEffect(() => {
        if (!isEdit && name && !slug) {
            const generated = name
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '');
            setSlug(generated);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name]);

    const createMutation = useMutation({
        mutationFn: (data: RedirectGroupCreate) => createRedirectGroup(data),
        onSuccess: () => {
            toast({ title: 'Grupo criado com sucesso!' });
            queryClient.invalidateQueries({ queryKey: ['redirect-groups'] });
            onOpenChange(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar grupo',
                description: error.response?.data?.detail || error.message,
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async () => {
            if (!group) throw new Error('Sem grupo');

            // Atualiza dados do grupo
            await updateRedirectGroup(group.id, {
                name,
                slug,
                redirect_type: redirectType,
                sync_mode: redirectType === 'smart' ? syncMode : null,
                period,
                min_impressions: minImpressions,
                min_pageviews: minPageviews,
                default_url: defaultUrl || null,
                is_active: isActive,
            });

            // Sincroniza items: cria, atualiza ou deleta
            for (const item of items) {
                if (item._deleted && item.id) {
                    await deleteRedirectGroupItem(item.id);
                } else if (!item.id) {
                    await addRedirectGroupItem(group.id, {
                        item_type: item.item_type,
                        domain: item.item_type === 'domain' ? item.domain : null,
                        manual_url: item.item_type === 'manual_url' ? item.manual_url : null,
                        allowed_paths:
                            item.item_type === 'domain' && item.allowed_paths.length > 0
                                ? item.allowed_paths
                                : null,
                        weight: item.weight,
                        is_active: item.is_active,
                    });
                } else {
                    await updateRedirectGroupItem(item.id, {
                        domain: item.item_type === 'domain' ? item.domain : null,
                        manual_url: item.item_type === 'manual_url' ? item.manual_url : null,
                        allowed_paths:
                            item.item_type === 'domain' && item.allowed_paths.length > 0
                                ? item.allowed_paths
                                : null,
                        weight: item.weight,
                        is_active: item.is_active,
                    });
                }
            }
        },
        onSuccess: () => {
            toast({ title: 'Grupo atualizado com sucesso!' });
            queryClient.invalidateQueries({ queryKey: ['redirect-groups'] });
            onOpenChange(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar grupo',
                description: error.response?.data?.detail || error.message,
            });
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            toast({ variant: 'destructive', title: 'Nome obrigatorio' });
            return;
        }
        if (!slug.trim()) {
            toast({ variant: 'destructive', title: 'Slug obrigatorio' });
            return;
        }
        if (redirectType === 'smart' && !syncMode) {
            toast({ variant: 'destructive', title: 'Sync mode obrigatorio para grupo smart' });
            return;
        }

        // Validacao dos items
        const validItems = items.filter((i) => !i._deleted);
        for (const item of validItems) {
            if (item.item_type === 'domain' && !item.domain.trim()) {
                toast({ variant: 'destructive', title: 'Dominio obrigatorio em todos os itens de tipo Domain' });
                return;
            }
            if (item.item_type === 'manual_url' && !item.manual_url.trim()) {
                toast({ variant: 'destructive', title: 'URL obrigatoria em todos os itens de tipo Manual URL' });
                return;
            }
        }

        if (isEdit) {
            updateMutation.mutate();
        } else {
            const itemsCreate: RedirectGroupItemCreate[] = validItems.map((i) => ({
                item_type: i.item_type,
                domain: i.item_type === 'domain' ? i.domain : null,
                manual_url: i.item_type === 'manual_url' ? i.manual_url : null,
                allowed_paths:
                    i.item_type === 'domain' && i.allowed_paths.length > 0
                        ? i.allowed_paths
                        : null,
                weight: i.weight,
                is_active: i.is_active,
            }));

            createMutation.mutate({
                name,
                slug,
                redirect_type: redirectType,
                sync_mode: redirectType === 'smart' ? syncMode : null,
                period,
                min_impressions: minImpressions,
                min_pageviews: minPageviews,
                default_url: defaultUrl || null,
                is_active: isActive,
                items: itemsCreate,
            });
        }
    };

    const updateItem = (index: number, patch: Partial<ItemDraft>) => {
        setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
    };

    // Total de pesos dos items ativos (nao deletados) - pra calcular %
    const totalWeight = items.reduce((sum, it) => {
        if (it._deleted) return sum;
        return sum + (Number(it.weight) || 0);
    }, 0);

    const getItemPercent = (item: ItemDraft): number => {
        if (item._deleted || totalWeight <= 0) return 0;
        return ((Number(item.weight) || 0) / totalWeight) * 100;
    };

    const removeItem = (index: number) => {
        setItems((prev) => {
            const item = prev[index];
            if (item.id) {
                // marca pra deletar no save
                return prev.map((it, i) => (i === index ? { ...it, _deleted: true } : it));
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const addItem = () => {
        setItems((prev) => [...prev, emptyItem()]);
    };

    const isPending = createMutation.isPending || updateMutation.isPending;
    const visibleItems = items.filter((i) => !i._deleted);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Editar Grupo' : 'Novo Grupo de Redirect'}</DialogTitle>
                    <DialogDescription>
                        Configure o grupo de redirect e os sites/URLs que farao parte do pool.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                    {/* Dados basicos */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Nome *</Label>
                            <Input
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Black Friday Top Sites"
                                disabled={isPending}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="slug">Slug *</Label>
                            <Input
                                id="slug"
                                value={slug}
                                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                placeholder="black-friday-top-sites"
                                disabled={isPending}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Tipo de Redirect *</Label>
                            <Select value={redirectType} onValueChange={(v) => setRedirectType(v as RedirectType)} disabled={isPending}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="smart">Smart (sync automatico RPS / eCPM)</SelectItem>
                                    <SelectItem value="manual">Manual (pesos fixos, sem sync)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {redirectType === 'smart' && (
                            <div className="space-y-2">
                                <Label>Modo de Sync *</Label>
                                <Select value={syncMode} onValueChange={(v) => setSyncMode(v as SyncMode)} disabled={isPending}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="rps">RPS (Receita por Sessao)</SelectItem>
                                        <SelectItem value="ecpm">eCPM</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    {/* Explicacao do tipo selecionado */}
                    <div
                        className={`rounded-lg border p-3 flex gap-2 ${
                            redirectType === 'smart'
                                ? 'bg-emerald-50/50 border-emerald-200'
                                : 'bg-amber-50/50 border-amber-200'
                        }`}
                    >
                        <Info
                            className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                                redirectType === 'smart' ? 'text-emerald-600' : 'text-amber-600'
                            }`}
                        />
                        <div
                            className={`text-xs space-y-1 ${
                                redirectType === 'smart' ? 'text-emerald-900' : 'text-amber-900'
                            }`}
                        >
                            {redirectType === 'smart' ? (
                                <>
                                    <p className="font-semibold">Smart - sync automatico</p>
                                    <p>
                                        Os dominios sao sincronizados automaticamente todo dia
                                        usando <strong>RPS</strong> ou <strong>eCPM</strong> real.
                                        As URLs mais lucrativas ganham mais peso sozinhas.
                                    </p>
                                    <p>
                                        Voce pode adicionar URLs manuais (com peso fixo) no mesmo
                                        grupo - elas nao entram no sync mas competem no pool.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="font-semibold">Manual - pesos fixos, SEM sync</p>
                                    <p>
                                        Nao busca RPS nem eCPM. Voce controla manualmente todos os
                                        pesos. Ideal pra testes A/B, campanhas especificas ou
                                        quando quer peso igual entre URLs.
                                    </p>
                                    <p>
                                        Para dominios, voce <strong>precisa</strong> selecionar
                                        URLs especificas (allowed paths) - dominios sem URLs
                                        selecionadas serao ignorados.
                                    </p>
                                </>
                            )}
                        </div>
                    </div>

                    {redirectType === 'smart' && (
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Periodo *</Label>
                                <Select value={period} onValueChange={(v) => setPeriod(v as Period)} disabled={isPending}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="yesterday">Ontem</SelectItem>
                                        <SelectItem value="last_3_days">Ultimos 3 dias</SelectItem>
                                        <SelectItem value="last_7_days">Ultimos 7 dias</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="min_impressions">Impressoes Minimas</Label>
                                <Input
                                    id="min_impressions"
                                    type="number"
                                    min={0}
                                    value={minImpressions}
                                    onChange={(e) => setMinImpressions(parseInt(e.target.value) || 0)}
                                    disabled={isPending}
                                />
                            </div>
                            {syncMode === 'rps' && (
                                <div className="space-y-2">
                                    <Label htmlFor="min_pageviews">Pageviews Minimos</Label>
                                    <Input
                                        id="min_pageviews"
                                        type="number"
                                        min={0}
                                        value={minPageviews}
                                        onChange={(e) => setMinPageviews(parseInt(e.target.value) || 0)}
                                        disabled={isPending}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="default_url">URL Default (fallback)</Label>
                        <Input
                            id="default_url"
                            value={defaultUrl}
                            onChange={(e) => setDefaultUrl(e.target.value)}
                            placeholder="https://exemplo.com/fallback"
                            disabled={isPending}
                        />
                        <p className="text-xs text-gray-500">Usado quando o pool esta vazio.</p>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border p-3">
                        <div>
                            <Label htmlFor="is_active">Ativo</Label>
                            <p className="text-xs text-gray-500">Grupos inativos nao redirecionam</p>
                        </div>
                        <Switch
                            id="is_active"
                            checked={isActive}
                            onCheckedChange={setIsActive}
                            disabled={isPending}
                        />
                    </div>

                    {/* ITEMS */}
                    <div className="space-y-3 border-t pt-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">Sites / URLs</h3>
                                <p className="text-xs text-gray-500">
                                    Adicione dominios (smart) e/ou URLs manuais com peso.
                                </p>
                            </div>
                            <Button type="button" variant="outline" size="sm" onClick={addItem} disabled={isPending}>
                                <Plus className="mr-1 h-4 w-4" />
                                Adicionar
                            </Button>
                        </div>

                        {/* Helper box explicando como o peso funciona */}
                        <div className="rounded-lg bg-blue-50/50 border border-blue-200 p-3 flex gap-2">
                            <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-blue-900 space-y-1">
                                <p className="font-semibold">Como funciona o peso:</p>
                                <p>
                                    O peso define a <strong>fatia de trafego</strong> de cada
                                    item. Quanto maior, mais redirects vao apontar pra ele.
                                </p>
                                <p>
                                    A porcentagem ao lado de cada peso mostra a{' '}
                                    <strong>chance de ser escolhido</strong> no redirect. Ex:
                                    peso 2 em 1 item de 4 com peso 1 = 2 / (1+1+1+2) ={' '}
                                    <strong>40%</strong>.
                                </p>
                                {redirectType === 'smart' ? (
                                    <p className="text-blue-700">
                                        <strong>Dentro de cada dominio</strong>, se voce selecionar
                                        URLs especificas, cada uma ganha sub-peso automatico pelo
                                        RPS real (ou peso manual se definido).
                                    </p>
                                ) : (
                                    <p className="text-blue-700">
                                        <strong>Modo Manual:</strong> dentro de cada dominio, cada
                                        URL selecionada recebe peso igual (ou peso customizado se
                                        definido). Nao ha sync automatico.
                                    </p>
                                )}
                            </div>
                        </div>

                        {visibleItems.length === 0 && (
                            <p className="text-center text-sm text-gray-400 py-4">
                                Nenhum item adicionado.
                            </p>
                        )}

                        {items.map((item, index) => {
                            if (item._deleted) return null;
                            return (
                                <div
                                    key={index}
                                    className="rounded-lg border p-3 bg-gray-50 space-y-3"
                                >
                                    <div className="grid grid-cols-12 gap-2 items-end">
                                        <div className="col-span-3 space-y-1">
                                            <Label className="text-xs">Tipo</Label>
                                            <Select
                                                value={item.item_type}
                                                onValueChange={(v) => updateItem(index, { item_type: v as ItemType })}
                                                disabled={isPending}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="domain">
                                                        <div className="flex items-center gap-2">
                                                            <Globe className="h-3 w-3" />
                                                            Domain
                                                        </div>
                                                    </SelectItem>
                                                    <SelectItem value="manual_url">
                                                        <div className="flex items-center gap-2">
                                                            <Link2 className="h-3 w-3" />
                                                            Manual URL
                                                        </div>
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="col-span-6 space-y-1">
                                            <Label className="text-xs">
                                                {item.item_type === 'domain' ? 'Dominio' : 'URL'}
                                            </Label>
                                            {item.item_type === 'domain' ? (
                                                <Select
                                                    value={item.domain}
                                                    onValueChange={(v) => updateItem(index, { domain: v })}
                                                    disabled={isPending}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Selecione um dominio" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {availableDomains.length === 0 && (
                                                            <div className="px-2 py-1.5 text-xs text-gray-500">
                                                                Nenhum dominio cadastrado
                                                            </div>
                                                        )}
                                                        {availableDomains
                                                            .filter((d) => d.is_active)
                                                            .map((d) => (
                                                                <SelectItem key={d.id} value={d.domain}>
                                                                    {d.domain}
                                                                </SelectItem>
                                                            ))}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <Input
                                                    value={item.manual_url}
                                                    onChange={(e) => updateItem(index, { manual_url: e.target.value })}
                                                    placeholder="https://exemplo.com/pagina"
                                                    disabled={isPending}
                                                />
                                            )}
                                        </div>

                                        <div className="col-span-2 space-y-1">
                                            <Label className="text-xs flex items-center justify-between">
                                                <span>Peso</span>
                                                <span className="text-[10px] font-semibold text-blue-600">
                                                    {getItemPercent(item).toFixed(1)}%
                                                </span>
                                            </Label>
                                            <Input
                                                type="number"
                                                min={1}
                                                value={item.weight}
                                                onChange={(e) =>
                                                    updateItem(index, { weight: parseInt(e.target.value) || 1 })
                                                }
                                                disabled={isPending}
                                                title={`${getItemPercent(item).toFixed(1)}% de chance`}
                                            />
                                        </div>

                                        <div className="col-span-1 flex justify-center">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => removeItem(index)}
                                                disabled={isPending}
                                            >
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Seletor de URLs especificas (apenas para item_type=domain) */}
                                    {item.item_type === 'domain' && item.domain && (
                                        <div className="flex items-start gap-2 pt-2 border-t border-gray-200">
                                            <div className="flex-1">
                                                <Label className="text-xs text-gray-600">
                                                    URLs permitidas
                                                </Label>
                                                <div className="mt-1 flex flex-wrap gap-1 min-h-[24px]">
                                                    {item.allowed_paths.length === 0 ? (
                                                        <span
                                                            className={`text-[11px] italic ${
                                                                redirectType === 'manual'
                                                                    ? 'text-amber-600 font-medium'
                                                                    : 'text-gray-400'
                                                            }`}
                                                        >
                                                            {redirectType === 'manual'
                                                                ? 'Obrigatorio: selecione URLs (modo Manual ignora dominios sem paths)'
                                                                : 'Todas as URLs do dominio serao consideradas no sync'}
                                                        </span>
                                                    ) : (
                                                        <>
                                                            {item.allowed_paths.slice(0, 6).map((ap, i) => (
                                                                <Badge
                                                                    key={i}
                                                                    variant="outline"
                                                                    className="text-[10px] py-0 h-5 font-mono"
                                                                    title={
                                                                        ap.weight != null
                                                                            ? `peso ${ap.weight}`
                                                                            : 'peso automatico (RPS)'
                                                                    }
                                                                >
                                                                    {ap.path}
                                                                    {ap.weight != null && (
                                                                        <span className="ml-1 text-blue-600 font-semibold">
                                                                            ·{ap.weight}
                                                                        </span>
                                                                    )}
                                                                </Badge>
                                                            ))}
                                                            {item.allowed_paths.length > 6 && (
                                                                <Badge
                                                                    variant="outline"
                                                                    className="text-[10px] py-0 h-5"
                                                                >
                                                                    +{item.allowed_paths.length - 6} mais
                                                                </Badge>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    setUrlSelectorItemIndex(index);
                                                    setUrlSelectorOpen(true);
                                                }}
                                                disabled={isPending}
                                            >
                                                <ListFilter className="mr-1 h-3 w-3" />
                                                {item.allowed_paths.length > 0
                                                    ? `Editar (${item.allowed_paths.length})`
                                                    : 'Selecionar URLs'}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Preview visual de distribuicao */}
                        {visibleItems.length > 1 && totalWeight > 0 && (
                            <div className="rounded-lg border border-gray-200 bg-white p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="text-xs text-gray-700 font-semibold">
                                        Preview de distribuicao
                                    </Label>
                                    <span className="text-[10px] text-gray-400">
                                        total: {totalWeight}
                                    </span>
                                </div>
                                {/* Barra de distribuicao unica */}
                                <div className="h-6 rounded-md overflow-hidden flex bg-gray-100 mb-2">
                                    {items.map((item, idx) => {
                                        if (item._deleted) return null;
                                        const pct = getItemPercent(item);
                                        if (pct <= 0) return null;
                                        const colors = [
                                            'bg-blue-500',
                                            'bg-emerald-500',
                                            'bg-purple-500',
                                            'bg-orange-500',
                                            'bg-pink-500',
                                            'bg-cyan-500',
                                            'bg-amber-500',
                                            'bg-indigo-500',
                                        ];
                                        const color = colors[idx % colors.length];
                                        const label =
                                            item.item_type === 'domain'
                                                ? item.domain || `dominio #${idx + 1}`
                                                : item.manual_url || `url #${idx + 1}`;
                                        return (
                                            <div
                                                key={idx}
                                                className={`${color} transition-all flex items-center justify-center text-[9px] font-bold text-white`}
                                                style={{ width: `${pct}%` }}
                                                title={`${label} - peso ${item.weight} (${pct.toFixed(1)}%)`}
                                            >
                                                {pct >= 8 && `${pct.toFixed(0)}%`}
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Legenda */}
                                <div className="space-y-1">
                                    {items.map((item, idx) => {
                                        if (item._deleted) return null;
                                        const pct = getItemPercent(item);
                                        const colors = [
                                            'bg-blue-500',
                                            'bg-emerald-500',
                                            'bg-purple-500',
                                            'bg-orange-500',
                                            'bg-pink-500',
                                            'bg-cyan-500',
                                            'bg-amber-500',
                                            'bg-indigo-500',
                                        ];
                                        const color = colors[idx % colors.length];
                                        const label =
                                            item.item_type === 'domain'
                                                ? item.domain || `dominio vazio`
                                                : item.manual_url || `url vazia`;
                                        return (
                                            <div
                                                key={idx}
                                                className="flex items-center gap-2 text-[11px]"
                                            >
                                                <span
                                                    className={`${color} h-2 w-2 rounded-full flex-shrink-0`}
                                                />
                                                <span className="flex-1 truncate font-mono text-gray-700">
                                                    {label}
                                                </span>
                                                <span className="text-gray-500">
                                                    peso {item.weight}
                                                </span>
                                                <span className="text-blue-600 font-semibold w-12 text-right">
                                                    {pct.toFixed(1)}%
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-3 pt-4 border-t">
                        <Button
                            type="button"
                            variant="outline"
                            className="flex-1"
                            onClick={() => onOpenChange(false)}
                            disabled={isPending}
                        >
                            Cancelar
                        </Button>
                        <Button type="submit" className="flex-1" disabled={isPending}>
                            {isPending ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar Grupo'}
                        </Button>
                    </div>
                </form>
            </DialogContent>

            {/* Seletor de URLs */}
            {urlSelectorItemIndex !== null && items[urlSelectorItemIndex] && (
                <UrlSelectorDialog
                    open={urlSelectorOpen}
                    onOpenChange={(o) => {
                        setUrlSelectorOpen(o);
                        if (!o) setUrlSelectorItemIndex(null);
                    }}
                    domain={items[urlSelectorItemIndex].domain}
                    selectedPaths={items[urlSelectorItemIndex].allowed_paths}
                    onConfirm={(paths) => {
                        if (urlSelectorItemIndex !== null) {
                            updateItem(urlSelectorItemIndex, { allowed_paths: paths });
                        }
                    }}
                />
            )}
        </Dialog>
    );
}
